import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import axios from "axios";

import { toast } from 'react-toastify';
import Tippy from "@tippyjs/react";
import Popup from "reactjs-popup";
import Select from 'react-select';
import Flatpickr from "react-flatpickr";

import PageSectionMenu from './PageSectionMenu';
import OfferV2SettlementApp from '../../OfferV2Settlement/startup/OfferV2SettlementApp';
import { actionErrorText, toastError } from '../../../shared/toastHelper';

import {
  offsetDateForBrowser, offsetDateForServer
} from '../../../shared/timeZoneLogic';

import '../styles/settlementsTab.css';

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const formatDate = (date) => {
  return offsetDateForBrowser(date).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

// reactjs-popup content defaults to a white background; pull it from the --sl-*
// tokens so popups/modals go dark on themed pages and stay white in light mode.
const modalContentStyle = {
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "800px",
  "padding": "0px",
  "background": "var(--sl-bg-primary)"
}

const popupContentStyle = {
  "border": "1px solid var(--sl-border-secondary)",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "250px",
  "padding": "0px",
  "background": "var(--sl-bg-primary)"
}

const markAsPaidModalContentStyle = {
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "450px",
  "padding": "0px",
  "background": "var(--sl-bg-primary)"
}

const getV3OfferName = (offer) => {
  return offer.performer?.artistable?.name || offer.event_name || 'Untitled';
};

const ManageSettlements = ({
  csrfToken,
  team,
  confirm,
  currentUser,
  offers,
  v3Offers,
  offerForSettlement,
  offerForSettlementChanged,
  confirmPageSection,
  fetchPermissionSet,
  updateConfirmPageSection,
  offerChanged,
  v3OfferChanged,
  fetchOfferTotalDue
}) => {
  const [markAsPaid, setMarkAsPaid] = useState({});
  const [markingAsPaid, setMarkingAsPaid] = useState(false);
  const [markV3AsPaid, setMarkV3AsPaid] = useState({});
  const [markingV3AsPaid, setMarkingV3AsPaid] = useState(false);
  const [markTaxWithholdingAsPaid, setMarkTaxWithholdingAsPaid] = useState({});
  const [markingTaxWithholdingAsPaid, setMarkingTaxWithholdingAsPaid] = useState(false);

  const markOfferAsPaid = (offer) => {
    setMarkingAsPaid(true);

    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    axios.patch("/teams/" + team.id + "/offers/" + offer.id, {
      offer: {
        settlement_paid_at: offsetDateForServer(markAsPaid.settlement_paid_at),
        settlement_payment_method: markAsPaid.settlement_payment_method
      }
    })
    .then(({ data }) => {
      offerChanged({
        ...offer,
        settlement_paid_at: markAsPaid.settlement_paid_at,
        settlement_payment_method: markAsPaid.settlement_payment_method,
        tax_withholding_settlements: data.tax_withholding_settlements
      })

      setMarkAsPaid({});
    })
    .catch((error) => {
      toast.error("Unable to mark offer as paid. Please try again.", {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 5000,
        hideProgressBar: true
      });
    })
    .finally(() => {
      setMarkingAsPaid(false);
    })
  }

  const markV3OfferAsPaid = (v3Offer) => {
    setMarkingV3AsPaid(true);

    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    axios.patch("/teams/" + team.id + "/offers/v3/" + v3Offer.id, {
      v3_offer: {
        settlement_paid_at: offsetDateForServer(markV3AsPaid.settlement_paid_at),
        settlement_payment_method: markV3AsPaid.settlement_payment_method
      }
    })
    .then(({ data }) => {
      v3OfferChanged({
        ...v3Offer,
        settlement_paid_at: markV3AsPaid.settlement_paid_at,
        settlement_payment_method: markV3AsPaid.settlement_payment_method
      })

      setMarkV3AsPaid({});
    })
    .catch((error) => {
      toastError(actionErrorText(error, "Unable to mark offer as paid. Please try again."));
    })
    .finally(() => {
      setMarkingV3AsPaid(false);
    })
  }

  const markTaxWithholdingSettlementAsPaid = (offer, taxWithholdingSettlement) => {
    setMarkingTaxWithholdingAsPaid(true);

    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.patch("/teams/" + team.id + "/offers/" + offer.id, {
      offer: {
        tax_withholding_settlements_attributes: [{
          id: taxWithholdingSettlement.id,
          paid_at: offsetDateForServer(markTaxWithholdingAsPaid.paid_at),
          payment_method: markTaxWithholdingAsPaid.payment_method
        }]
      }
    })
      .then(({ data }) => {
        offerChanged({
          ...offer,
          tax_withholding_settlements: data.tax_withholding_settlements
        })

        setMarkTaxWithholdingAsPaid({});
      })
      .catch((error) => {
        toastError("Unable to mark tax withholding settlement as paid. Please try again.");
      })
      .finally(() => {
        setMarkingTaxWithholdingAsPaid(false);
      })
  }

  const settlementV3Offers = (v3Offers || []).filter((v3Offer) => (v3Offer.counts_toward_show ?? true) || v3Offer.settlement_paid_at);
  const settlementCount = (offers || []).length + settlementV3Offers.length;

  const [view, setView] = useState(() => {
    if (typeof window === "undefined") return "cards";
    return window.localStorage.getItem("sl-settlements-view") === "table" ? "table" : "cards";
  });
  const selectView = (next) => {
    setView(next);
    if (typeof window !== "undefined") window.localStorage.setItem("sl-settlements-view", next);
  };

  const menuWrap = (items) => (
    <Popup arrow={false} offsetY={5} position="bottom right" contentStyle={popupContentStyle}
      onOpen={(e) => e.preventDefault()}
      trigger={() => (<a href="#" className="pl-3" onClick={(e) => e.preventDefault()}><i className="fas fa-ellipsis-v"></i></a>)}>
      {close => (<div className="row text-left"><div className="col-12"><ul className="list-group offer-inline-menu">{items(close)}</ul></div></div>)}
    </Popup>
  );
  const markPaidItem = (onClick) => (
    <li className="list-group-item">
      <a href="#" className="text-muted" onClick={onClick}>
        <span className="d-inline-block text-center" style={{ width: "28px" }}><i className="far fa-money-bill"></i></span>
        <span style={{ color: "var(--sl-text-primary)" }}>Mark as Paid</span>
      </a>
    </li>
  );
  const linkItem = (href, icon, label, blank) => (
    <li className="list-group-item">
      <a href={href} className="text-muted" {...(blank ? { target: "_blank" } : {})}>
        <span className="d-inline-block text-center" style={{ width: "28px" }}><i className={icon}></i></span>
        <span style={{ color: "var(--sl-text-primary)" }}>{label}</span>
      </a>
    </li>
  );
  const offerMenu = (offer) => currentUser.can_manage_offers ? menuWrap((close) => (<>
    {offer.settlement_paid_at === null ? markPaidItem((e) => { e.preventDefault(); close(); setMarkAsPaid({ offer }); }) : null}
    {linkItem(`/teams/${team.id}/offers/${offer.id}/settlement`, "fas fa-pencil", "Edit")}
    {linkItem(`/teams/${team.id}/offers/${offer.id}/settlement.pdf`, "fas fa-download", "Download PDF", true)}
  </>)) : null;
  const v3OfferMenu = (v3Offer) => currentUser.can_manage_offers ? menuWrap((close) => (<>
    {v3Offer.settlement_paid_at === null ? markPaidItem((e) => { e.preventDefault(); close(); setMarkV3AsPaid({ v3Offer }); }) : null}
    {linkItem(`/teams/${team.id}/offers/v3/${v3Offer.id}/settlement`, "fas fa-pencil", "Edit")}
    {linkItem(`/teams/${team.id}/offers/v3/${v3Offer.id}/settlement.pdf`, "fas fa-download", "Download PDF", true)}
  </>)) : null;
  const taxMenu = (offer, tws) => (currentUser.can_manage_offers && tws.paid_at === null)
    ? menuWrap((close) => markPaidItem((e) => { e.preventDefault(); close(); setMarkTaxWithholdingAsPaid({ offer, taxWithholdingSettlement: tws }); }))
    : null;

  const paidPill = (paidAt, info) => paidAt
    ? <Tippy content={info}><span className="badge badge-success px-2 py-1">Paid</span></Tippy>
    : <span className="text-muted">—</span>;
  const money = (v) => (v != null ? formatter.format(v) : <span className="sl-settlement-value--empty">--</span>);

  // Normalized rows for the table (settlements + tax withholdings + v3), in the
  // same order the cards render.
  const rows = [];
  offers.slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).forEach((offer) => {
    rows.push({ key: `offer-${offer.id}`, name: offer.artist_or_event_name, type: "Settlement", isV3: false,
      total: offer.total_due, paidAt: offer.settlement_paid_at,
      paidInfo: offer.settlement_paid_at ? `Paid with ${offer.settlement_payment_method} on ${formatDate(offer.settlement_paid_at)}` : null,
      editHref: `/teams/${team.id}/offers/${offer.id}/settlement`,
      menu: offerMenu(offer) });
    if (offer.settlement_paid_at === null && offer.budget?.has_tax_withholding) {
      rows.push({ key: `twp-${offer.id}`, name: offer.artist_or_event_name, type: "Tax Withholding", isV3: false,
        total: offer.tax_withholding, paidAt: null, paidInfo: null, menu: null });
    }
    offer.tax_withholding_settlements.forEach((tws) => {
      rows.push({ key: `tws-${tws.id}`, name: offer.artist_or_event_name, type: "Tax Withholding", isV3: false,
        total: tws.amount, paidAt: tws.paid_at,
        paidInfo: tws.paid_at ? `Paid on ${formatDate(tws.paid_at)}` : null,
        menu: taxMenu(offer, tws) });
    });
  });
  settlementV3Offers.slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).forEach((v3Offer) => {
    rows.push({ key: `v3-${v3Offer.id}`, name: v3Offer.artist_or_event_name || getV3OfferName(v3Offer), type: "Settlement", isV3: true,
      total: v3Offer.total_due, paidAt: v3Offer.settlement_paid_at,
      paidInfo: v3Offer.settlement_paid_at ? `Paid with ${v3Offer.settlement_payment_method} on ${formatDate(v3Offer.settlement_paid_at)}` : null,
      editHref: `/teams/${team.id}/offers/v3/${v3Offer.id}/settlement`,
      menu: v3OfferMenu(v3Offer) });
  });

  const settlementsTable = (
    <div className="sl-settlements-table-wrap">
      <table className="sl-settlements-table">
        <thead>
          <tr>
            <th>Settlement For</th>
            <th>Type</th>
            <th>Total Due</th>
            <th>Status</th>
            {currentUser.can_manage_offers ? <th className="sl-settlements-table-actions" aria-label="Actions"></th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.key}
              className={row.editHref ? "sl-settlement-row" : ""}
              onClick={row.editHref ? () => { window.location.href = row.editHref; } : undefined}
            >
              <td>
                <span className="sl-settlement-name-inline">{row.name}</span>
                {row.isV3 ? <span className="badge badge-light ml-1">V3</span> : null}
              </td>
              <td>{row.type}</td>
              <td>{money(row.total)}</td>
              <td>{paidPill(row.paidAt, row.paidInfo)}</td>
              {currentUser.can_manage_offers ? (
                <td className="sl-settlements-table-actions" onClick={(e) => e.stopPropagation()}>{row.menu}</td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const settlementsCards = (
    <div className="sl-settlements-grid">
      {rows.map((row) => (
        <div
          key={row.key}
          className={`sl-settlement-card${row.editHref ? " sl-settlement-card--clickable" : ""}`}
          onClick={row.editHref ? () => { window.location.href = row.editHref; } : undefined}
        >
          <div className="sl-settlement-card-head">
            <div style={{ minWidth: 0 }}>
              <div className="sl-settlement-label">
                {row.type} For{row.isV3 ? <span className="badge badge-light ml-1">V3</span> : null}
              </div>
              <div className="sl-settlement-name" title={row.name}>{row.name}</div>
            </div>
            <div className="sl-settlement-card-head-aside" onClick={(e) => e.stopPropagation()}>
              {row.paidAt ? (
                <Tippy content={row.paidInfo}>
                  <span className="px-3 py-2 badge badge-success">Paid</span>
                </Tippy>
              ) : null}
              {row.menu}
            </div>
          </div>
          <div className="sl-settlement-total">
            <div className="sl-settlement-label">Total Due</div>
            <div className="sl-settlement-value">{money(row.total)}</div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="sl-settlements">
      <div className="sl-settlements-header">
        <h5 className="sl-settlements-title">
          Settlements
          {settlementCount > 0 ? <span className="sl-settlements-count">{settlementCount}</span> : null}
          {confirmPageSection.permission_set_id ? (
            <i className="ml-2 far fa-lock small sl-settlement-lock"></i>
          ) : null}
        </h5>
        <div className="sl-settlements-header-actions">
          {settlementCount > 0 ? (
            <div className="sl-settlements-view-toggle" role="group" aria-label="Settlements view">
              <button type="button" className={`sl-settlements-view-btn ${view === "table" ? "active" : ""}`.trim()}
                onClick={() => selectView("table")} title="Table view" aria-label="Table view">
                <i className="fas fa-list"></i>
              </button>
              <button type="button" className={`sl-settlements-view-btn ${view === "cards" ? "active" : ""}`.trim()}
                onClick={() => selectView("cards")} title="Card view" aria-label="Card view">
                <i className="fas fa-th-large"></i>
              </button>
            </div>
          ) : null}
          <PageSectionMenu csrfToken={csrfToken}
                           team={team}
                           confirm={confirm}
                           currentUser={currentUser}
                           confirmPageSection={confirmPageSection}
                           fetchPermissionSet={fetchPermissionSet}
                           updateConfirmPageSection={updateConfirmPageSection} />
        </div>
      </div>
      {view === "table" ? settlementsTable : settlementsCards}
      <Popup
        open={Object.keys(offerForSettlement).length > 0}
        contentStyle={modalContentStyle}
        closeOnDocumentClick={false}
        className={"offer-modal"}
        onClose={() => {
          offerForSettlementChanged({});
        }}
      >
        <OfferV2SettlementApp offer={offerForSettlement}
                              currentUser={currentUser}
                              csrfToken={csrfToken}
                              onClose={
                                () => {
                                  fetchOfferTotalDue(team, offerForSettlement);
                                  offerForSettlementChanged({});
                                }
                              }
                              team={team} />
      </Popup>
      <Popup
        open={!!markAsPaid.offer}
        modal={true}
        lockScroll={true}
        contentStyle={markAsPaidModalContentStyle}
        closeOnDocumentClick={true}
        className={"offer-modal"}
        onClose={() => {
          setMarkAsPaid({});
        }}
      >
        <div className="m-0">
          <div className="p-4">
            <div
              className="mb-2 d-flex align-items-center justify-content-between"
              style={{ fontSize: "18px" }}
            >
              <strong>
                Mark settlement as paid
              </strong>
              <a
                href="#"
                onClick={e => {
                  e.preventDefault();
                  setMarkAsPaid({});
                }}
              >
                <i className="fas fa-times text-muted" />
              </a>
            </div>
            <div className="form-row">
              <div className="col">
                <label className="mb-1">
                  Date Paid
                  <span className="ml-1 text-danger">*</span>
                </label>
                <div
                  className="flatpickr-date-input px-2 d-flex align-items-center"
                  style={{
                    border: "1px solid var(--sl-border-primary)",
                    borderRadius: "8px",
                  }}
                >
                  <div className="pr-2" style={{"fontSize": "18px"}}>
                    <i className="far fa-calendar"></i>
                  </div>
                  <Flatpickr
                    options={{ dateFormat: "l, F j Y" }}
                    placeholder={"Select a date"}
                    value={markAsPaid.settlement_paid_at}
                    onClose={dates => {
                      setMarkAsPaid({
                        ...markAsPaid,
                        settlement_paid_at: dates[0]
                      });
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="mt-3 form-row">
              <div className="col">
                <label className="mb-1">
                  Payment Method
                  <span className="ml-1 text-danger">*</span>
                </label>
                <Select
                  placeholder="Choose a payment method"
                  options={[
                    { value: 'Check', label: 'Check' },
                    { value: 'ACH/Wire', label: 'ACH/Wire' },
                    { value: 'Cash', label: 'Cash' },
                    { value: 'Other', label: 'Other' }
                  ]}
                  value={markAsPaid.settlement_payment_method ? ({
                    value: markAsPaid.settlement_payment_method,
                    label: markAsPaid.settlement_payment_method
                  }) : null}
                  onChange={option => {
                    setMarkAsPaid({
                      ...markAsPaid,
                      settlement_payment_method: option.value
                    });
                  }}
                />
              </div>
            </div>
            <div className="mt-4 form-row">
              <div className="col-6">
                <button
                  className="w-100 btn btn-light"
                  style={{
                    border: "1px solid var(--sl-border-primary)"
                  }}
                  onClick={e => {
                    e.preventDefault();
                    setMarkAsPaid({});
                  }}
                >
                  Cancel
                </button>
              </div>
              <div className="col-6">
                <button
                  disabled={markingAsPaid || !markAsPaid.settlement_paid_at || !markAsPaid.settlement_payment_method}
                  className="btn btn-primary w-100"
                  onClick={e => {
                    e.preventDefault();

                    markOfferAsPaid(markAsPaid.offer);
                  }}
                >
                  {markingAsPaid ? (
                    <div
                      className="spinner-border spinner-border-sm"
                      style={{ marginBottom: "2px" }}
                    >
                      <span className="sr-only">Loading...</span>
                    </div>
                  ) : (
                    "Mark as Paid"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Popup>
      <Popup
        open={!!markTaxWithholdingAsPaid.offer}
        modal={true}
        lockScroll={true}
        contentStyle={markAsPaidModalContentStyle}
        closeOnDocumentClick={true}
        className={"offer-modal"}
        onClose={() => {
          setMarkTaxWithholdingAsPaid({});
        }}
      >
        <div className="m-0">
          <div className="p-4">
            <div
              className="mb-2 d-flex align-items-center justify-content-between"
              style={{ fontSize: "18px" }}
            >
              <strong>
                Mark tax withholding as paid
              </strong>
              <a
                href="#"
                onClick={e => {
                  e.preventDefault();
                  setMarkTaxWithholdingAsPaid({});
                }}
              >
                <i className="fas fa-times text-muted" />
              </a>
            </div>
            <div className="form-row">
              <div className="col">
                <label className="mb-1">
                  Date Paid
                  <span className="ml-1 text-danger">*</span>
                </label>
                <div
                  className="flatpickr-date-input px-2 d-flex align-items-center"
                  style={{
                    border: "1px solid var(--sl-border-primary)",
                    borderRadius: "8px",
                  }}
                >
                  <div className="pr-2" style={{"fontSize": "18px"}}>
                    <i className="far fa-calendar"></i>
                  </div>
                  <Flatpickr
                    options={{ dateFormat: "l, F j Y" }}
                    placeholder={"Select a date"}
                    value={markTaxWithholdingAsPaid.paid_at}
                    onClose={dates => {
                      setMarkTaxWithholdingAsPaid({
                        ...markTaxWithholdingAsPaid,
                        paid_at: dates[0]
                      });
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="mt-3 form-row">
              <div className="col">
                <label className="mb-1">
                  Payment Method
                  <span className="ml-1 text-danger">*</span>
                </label>
                <Select
                  placeholder="Choose a payment method"
                  options={[
                    { value: 'Check', label: 'Check' },
                    { value: 'ACH/Wire', label: 'ACH/Wire' },
                    { value: 'Cash', label: 'Cash' },
                    { value: 'Other', label: 'Other' }
                  ]}
                  value={markTaxWithholdingAsPaid.payment_method ? ({
                    value: markTaxWithholdingAsPaid.payment_method,
                    label: markTaxWithholdingAsPaid.payment_method
                  }) : null}
                  onChange={option => {
                    setMarkTaxWithholdingAsPaid({
                      ...markTaxWithholdingAsPaid,
                      payment_method: option.value
                    });
                  }}
                />
              </div>
            </div>
            <div className="mt-4 form-row">
              <div className="col-6">
                <button
                  className="w-100 btn btn-light"
                  style={{
                    border: "1px solid var(--sl-border-primary)"
                  }}
                  onClick={e => {
                    e.preventDefault();
                    setMarkTaxWithholdingAsPaid({});
                  }}
                >
                  Cancel
                </button>
              </div>
              <div className="col-6">
                <button
                  disabled={markingTaxWithholdingAsPaid || !markTaxWithholdingAsPaid.paid_at || !markTaxWithholdingAsPaid.payment_method}
                  className="btn btn-primary w-100"
                  onClick={e => {
                    e.preventDefault();

                    markTaxWithholdingSettlementAsPaid(markTaxWithholdingAsPaid.offer, markTaxWithholdingAsPaid.taxWithholdingSettlement);
                  }}
                >
                  {markingTaxWithholdingAsPaid ? (
                    <div
                      className="spinner-border spinner-border-sm"
                      style={{ marginBottom: "2px" }}
                    >
                      <span className="sr-only">Loading...</span>
                    </div>
                  ) : (
                    "Mark as Paid"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Popup>
      <Popup
        open={!!markV3AsPaid.v3Offer}
        modal={true}
        lockScroll={true}
        contentStyle={markAsPaidModalContentStyle}
        closeOnDocumentClick={true}
        className={"offer-modal"}
        onClose={() => {
          setMarkV3AsPaid({});
        }}
      >
        <div className="m-0">
          <div className="p-4">
            <div
              className="mb-2 d-flex align-items-center justify-content-between"
              style={{ fontSize: "18px" }}
            >
              <strong>
                Mark settlement as paid
              </strong>
              <a
                href="#"
                onClick={e => {
                  e.preventDefault();
                  setMarkV3AsPaid({});
                }}
              >
                <i className="fas fa-times text-muted" />
              </a>
            </div>
            <div className="form-row">
              <div className="col">
                <label className="mb-1">
                  Date Paid
                  <span className="ml-1 text-danger">*</span>
                </label>
                <div
                  className="flatpickr-date-input px-2 d-flex align-items-center"
                  style={{
                    border: "1px solid var(--sl-border-primary)",
                    borderRadius: "8px",
                  }}
                >
                  <div className="pr-2" style={{"fontSize": "18px"}}>
                    <i className="far fa-calendar"></i>
                  </div>
                  <Flatpickr
                    options={{ dateFormat: "l, F j Y" }}
                    placeholder={"Select a date"}
                    value={markV3AsPaid.settlement_paid_at}
                    onClose={dates => {
                      setMarkV3AsPaid({
                        ...markV3AsPaid,
                        settlement_paid_at: dates[0]
                      });
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="mt-3 form-row">
              <div className="col">
                <label className="mb-1">
                  Payment Method
                  <span className="ml-1 text-danger">*</span>
                </label>
                <Select
                  placeholder="Choose a payment method"
                  options={[
                    { value: 'Check', label: 'Check' },
                    { value: 'ACH/Wire', label: 'ACH/Wire' },
                    { value: 'Cash', label: 'Cash' },
                    { value: 'Other', label: 'Other' }
                  ]}
                  value={markV3AsPaid.settlement_payment_method ? ({
                    value: markV3AsPaid.settlement_payment_method,
                    label: markV3AsPaid.settlement_payment_method
                  }) : null}
                  onChange={option => {
                    setMarkV3AsPaid({
                      ...markV3AsPaid,
                      settlement_payment_method: option.value
                    });
                  }}
                />
              </div>
            </div>
            <div className="mt-4 form-row">
              <div className="col-6">
                <button
                  className="w-100 btn btn-light"
                  style={{
                    border: "1px solid var(--sl-border-primary)"
                  }}
                  onClick={e => {
                    e.preventDefault();
                    setMarkV3AsPaid({});
                  }}
                >
                  Cancel
                </button>
              </div>
              <div className="col-6">
                <button
                  disabled={markingV3AsPaid || !markV3AsPaid.settlement_paid_at || !markV3AsPaid.settlement_payment_method}
                  className="btn btn-primary w-100"
                  onClick={e => {
                    e.preventDefault();

                    markV3OfferAsPaid(markV3AsPaid.v3Offer);
                  }}
                >
                  {markingV3AsPaid ? (
                    <div
                      className="spinner-border spinner-border-sm"
                      style={{ marginBottom: "2px" }}
                    >
                      <span className="sr-only">Loading...</span>
                    </div>
                  ) : (
                    "Mark as Paid"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Popup>
    </div>
  );
};

ManageSettlements.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  offers: PropTypes.array,
  v3Offers: PropTypes.array,
  offerForSettlement: PropTypes.object,
  offerForSettlementChanged: PropTypes.func.isRequired,
  confirmPageSection: PropTypes.object,
  fetchPermissionSet: PropTypes.func.isRequired,
  updateConfirmPageSection: PropTypes.func.isRequired,
  offerChanged: PropTypes.func.isRequired,
  v3OfferChanged: PropTypes.func,
  fetchOfferTotalDue: PropTypes.func.isRequired
};

export default ManageSettlements;
